import isNull from 'lodash/isNull';
import type {
  StackBarsEntities,
  ComputedColumn,
  ColumnStackValues,
} from './types';
import type { ScaleLinear, ScaleBand, ScaleTime } from 'd3-scale';

type CalcColumnYValuesParams = {
  columnValues: ColumnStackValues;
  columnHeightValues: number[];
};

export const calcColumnYValues = ({
  columnValues,
  columnHeightValues,
}: CalcColumnYValuesParams) => {
  return columnValues.map((value, i) => {
    const isNotPositive = !value || value <= 0;
    if (isNotPositive) {
      return columnHeightValues
        .slice(0, i)
        .filter((n) => n >= 0)
        .reduce((acc, value) => acc + value, 0);
    }

    return columnHeightValues
      .slice(0, i + 1)
      .filter((n) => n < 0)
      .reduce((acc, value) => acc + value, 0);
  });
};

type SimpleScale = (key: string | number) => number | undefined;

type ComputeColumnsParams = {
  columnKeys: (string | number)[];
  barKeys: string[];
  entities: StackBarsEntities;
  xScale: ScaleBand<string> | ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  chartWidth: number;
  colors: Record<string, string>;
  entitiesFormatted?: Record<string, string>;
  chartColumnsCap?: number;
};

export const computeColumns = ({
  columnKeys,
  barKeys,
  entities,
  xScale,
  yScale,
  chartWidth,
  colors,
  entitiesFormatted = {},
  chartColumnsCap = columnKeys.length,
}: ComputeColumnsParams) => {
  const zero = yScale(0);
  const columnWidth = chartWidth > 0 ? chartWidth / chartColumnsCap : 0;
  const DEFAULT_COLUMN_PADDING = 8;
  const dynamicColumnPadding =
    columnWidth > 4 * DEFAULT_COLUMN_PADDING
      ? DEFAULT_COLUMN_PADDING
      : columnWidth * 0.2;

  return columnKeys.map((key): ComputedColumn => {
    const columnValues = entities[key];

    const formattedValue = entitiesFormatted[key] || null;
    const columnX = (xScale as SimpleScale)(key) || 0;

    if (!columnValues) {
      return {
        key: key.toString(),
        x: columnX,
        width: columnWidth,
        top: zero,
        formattedValue,
        bars: [],
      };
    }

    const columnHeightValues = columnValues.map((value) => {
      return isNull(value) ? 0 : (yScale(value) as number) - zero;
    });

    const columnYValues = calcColumnYValues({
      columnValues,
      columnHeightValues,
    });

    const columnTop = Math.min(...columnYValues);

    return {
      key: key.toString(),
      x: columnX,
      width: columnWidth,
      top: columnTop + zero,
      formattedValue,
      bars: barKeys.map((columnKey, columnIdx) => {
        const value = columnValues[columnIdx];
        return {
          key: columnKey,
          value,
          width: columnWidth - dynamicColumnPadding * 2,
          x: columnX + dynamicColumnPadding,
          y: columnYValues[columnIdx] + zero,
          color: colors[columnKey],
          height: Math.abs(columnHeightValues[columnIdx] || 0),
        };
      }),
    };
  });
};
